import { useState, useEffect, useCallback, RefObject } from 'react';

export interface TouchState {
    upPressed: boolean;
    downPressed: boolean;
}

/**
 * Custom hook for tracking touch/click input state
 * 
 * Manages touch/mouse input by dividing the screen into top and bottom halves:
 * - Top half: triggers "up" (rewind/reverse)
 * - Bottom half: triggers "down" (forward)
 * 
 * @param containerRef Reference to the container element
 * @returns Touch state object with upPressed and downPressed booleans
 */
export function useTouchInput(containerRef: RefObject<HTMLDivElement>): TouchState {
    const [touchState, setTouchState] = useState<TouchState>({
        upPressed: false,
        downPressed: false,
    });

    const handlePress = useCallback((clientY: number) => {
        if (!containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const relativeY = clientY - rect.top;
        const containerHeight = rect.height;
        const midpoint = containerHeight / 2;

        if (relativeY < midpoint) {
            // Top half - go upstairs (reverse)
            setTouchState({ upPressed: true, downPressed: false });
        } else {
            // Bottom half - go downstairs (forward)
            setTouchState({ upPressed: false, downPressed: true });
        }
    }, [containerRef]);

    const handleRelease = useCallback(() => {
        setTouchState({ upPressed: false, downPressed: false });
    }, []);

    const handleMouseDown = useCallback((event: MouseEvent) => {
        event.preventDefault();
        handlePress(event.clientY);
    }, [handlePress]);

    const handleMouseUp = useCallback((event: MouseEvent) => {
        event.preventDefault();
        handleRelease();
    }, [handleRelease]);

    const handleTouchStart = useCallback((event: TouchEvent) => {
        event.preventDefault();
        if (event.touches.length > 0) {
            handlePress(event.touches[0].clientY);
        }
    }, [handlePress]);

    const handleTouchEnd = useCallback((event: TouchEvent) => {
        event.preventDefault();
        handleRelease();
    }, [handleRelease]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        container.addEventListener('mousedown', handleMouseDown);
        window.addEventListener('mouseup', handleMouseUp);
        container.addEventListener('touchstart', handleTouchStart, { passive: false });
        window.addEventListener('touchend', handleTouchEnd, { passive: false });

        return () => {
            container.removeEventListener('mousedown', handleMouseDown);
            window.removeEventListener('mouseup', handleMouseUp);
            container.removeEventListener('touchstart', handleTouchStart);
            window.removeEventListener('touchend', handleTouchEnd);
        };
    }, [handleMouseDown, handleMouseUp, handleTouchStart, handleTouchEnd, containerRef]);

    return touchState;
}
